import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetcampaigndetailbyidQuery,
  useGetrcscampaiononlydataQuery,
  useGetrcsclickreportQuery,
  useLazyRcsdeliveryreportdownloadzipQuery,
} from "../store/services/rcstemplateApi";
import { cellSX, layoutCSS } from "../component/common/commonStyle";

// ─── Category config (matching the overview keys from the API) ───
const CATEGORY_CONFIG = [
  { key: "Total", overviewKey: "total", color: "default" },
  { key: "Sent", overviewKey: "sent", color: "info" },
  { key: "Delivered", overviewKey: "delivered", color: "success" },
  { key: "Read", overviewKey: "read", color: "primary" },
  { key: "Pending", overviewKey: "pending", color: "warning" },
  { key: "Failed", overviewKey: "failed", color: "error" }, // the API might already have a combined "failed" field
];

const RcsCampaignDetailsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [detailPage, setDetailPage] = useState(1);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);
  const [rcsStatusFilter, setRcsStatusFilter] = useState("Total");

  const [clickReportOpen, setClickReportOpen] = useState(false);
  const [clickReportMobile, setClickReportMobile] = useState(null);
  const [clickReportPage, setClickReportPage] = useState(1);
  const [clickReportRowsPerPage, setClickReportRowsPerPage] = useState(10);

  const [triggerZipDownload, { isLoading: zipDownloadLoading }] =
    useLazyRcsdeliveryreportdownloadzipQuery();

  // ─── Campaign info + overview (always fetched without status filter) ───
  const {
    data: campaignDetails,
    isLoading: loadingDetails,
    isFetching: loadingDetailsFetching,
  } = useGetcampaigndetailbyidQuery(
    { campaignId, page: detailPage, pagesize: detailRowsPerPage },
    { skip: !campaignId }
  );

  // ─── Message list – now with server‑side status filtering ───
  const {
    data: rcsMessageDataRes,
    isLoading: rcsMessageLoading,
    isFetching: rcsMessageFetching,
  } = useGetrcscampaiononlydataQuery(
    {
      campaign_id: campaignId,
      page: detailPage,
      pagesize: detailRowsPerPage,
      // pass status only when a specific filter is selected (skip for "Total")
      ...(rcsStatusFilter !== "Total" && { status: rcsStatusFilter.toLowerCase() }),
    },
    { skip: !campaignId }
  );

  // Click report query (unchanged)
  const {
    data: rcsClickReport,
    isLoading: rcsClickReportLoading,
    isFetching: rcsClickReportFetching,
    error: rcsClickReportError,
  } = useGetrcsclickreportQuery(
    {
      campaign_id: campaignId,
      mobile_number: clickReportMobile,
      page: clickReportPage,
      limit: clickReportRowsPerPage,
    },
    { skip: !campaignId || !clickReportMobile }
  );

  // ─── Reset page to 1 whenever the status filter changes ───
  useEffect(() => {
    setDetailPage(1);
  }, [rcsStatusFilter]);

  const campaign = campaignDetails?.campaign;
  const overview = campaignDetails?.overview;

  // ─── Chip labels derived from the campaign‑wide overview ───
  const categories = useMemo(() => {
    if (!overview) return CATEGORY_CONFIG.map(cat => ({ ...cat, count: 0, label: `${cat.key} (0)` }));
    return CATEGORY_CONFIG.map(cat => {
      let count = 0;
      if (cat.key === "Total") {
        count = overview.total ?? 0;
      } else if (cat.key === "Failed") {
        // some APIs return a combined "failed" key; if not, sum the sub‑failures
        count =
          (overview.failed ?? 0) 
        
      } else {
        count = overview[cat.overviewKey] ?? 0;
      }
      return { ...cat, count, label: `${cat.key} (${count})` };
    });
  }, [overview]);

  // ─── Data for the table comes from the status‑filtered query ───
  const rcsMessageData = rcsMessageDataRes?.data || [];
  const rcsPagination = rcsMessageDataRes?.pagination || {};

  // Helper functions (unchanged)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = dayjs(dateString);
    if (!d.isValid()) return "N/A";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
          {d.format("DD/MM/YYYY")}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
          {d.format("HH:mm")}
        </Typography>
      </Box>
    );
  };

  const normalizePhoneNumber = (phone) => {
    if (phone === null || phone === undefined) return "";
    if (typeof phone === "string" || typeof phone === "number") return String(phone);
    if (Array.isArray(phone)) return phone.map(normalizePhoneNumber).filter(Boolean).join(", ");
    if (typeof phone === "object") {
      return (
        phone.phone || phone.number || phone.id || phone.msisdn || phone.to || phone.from || JSON.stringify(phone)
      );
    }
    return String(phone);
  };

  const maskPhone = (num) => {
    const str = normalizePhoneNumber(num);
    if (!str) return "";
    return str.slice(0, 3) + "****" + str.slice(-3);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered": return "success";
      case "read": return "primary";
      case "sent": return "info";
      case "failed": return "error";
      case "EXECUTED": return "success";
      default: return "default";
    }
  };

  const handleDownloadByCampaignId = async () => {
    if (!campaignId) return toast.error("No campaign selected");
    try {
      const loadingToast = toast.loading("Preparing ZIP download...");
      const result = await triggerZipDownload({ campaign_id: campaignId }).unwrap();
      toast.dismiss(loadingToast);
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `campaign_${campaignId}_report.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started successfully!");
    } catch (err) {
      console.error("Campaign download failed:", err);
      toast.error(err?.data?.message || "Failed to download campaign report");
    }
  };

  return (
    <Box sx={{ ...layoutCSS, py: 1 }}>
      {/* Header – unchanged */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={() => navigate("/rcs-campaign-list")}><ArrowBackIcon fontSize="small" /></IconButton>
          <Typography variant="subtitle2" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            Campaign #{campaignId}
          </Typography>
        </Box>
        {/* <Button variant="contained" size="small" onClick={handleDownloadByCampaignId} disabled={zipDownloadLoading} sx={{ fontSize: "0.7rem", py: 0.3 }}>
          {zipDownloadLoading ? "ZIP..." : "Download ZIP"}
        </Button> */}
      </Box>

      {loadingDetails && !campaign ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={200}>
          <CircularProgress size={24} />
        </Box>
      ) : campaign ? (
        <>
          {/* Overview – unchanged */}
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                  Campaign Info
                </Typography>
                <Grid container spacing={0.5}>
                  {[
                    ["Name", campaign?.campaign_name],
                    ["Bot", campaign?.bot_name],
                    ["Template", campaign?.template_name],
                    ["Status", <Chip label={campaign?.status} size="small" color={getStatusColor(campaign?.status)} sx={{ fontSize: "0.65rem" }} />],
                    ["Created", formatDate(campaign?.created_at)],
                    ["Executed", formatDate(campaign?.executed_at)]
                  ].map(([label, value], i) => (
                    <Grid item xs={6} key={i}>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: "block", fontSize: "0.7rem" }}>{label}:</Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                  Message Statistics
                </Typography>
                {[
                  { title: "Submission", total: overview?.total, items: [
                    ["Total", overview?.total], ["Submit", overview?.submit], ["Sent", overview?.sent], ["Pending", overview?.pending]
                  ]},
                  { title: "Delivered", total: overview?.delivered, items: [
                    ["Delivered", overview?.delivered], ["Read", overview?.read], ["Click", overview?.click]
                  ]},
                  { title: "Failed", total: overview?.failed, items: [
                    ["Disabled", overview?.disabled], ["Limit Exhausted", overview?.limitExhausted],
                    ["Rejected", overview?.rejected], ["Other", overview?.otherFailed]
                  ]},
                ].map((section, secIdx) => (
                  <Box key={secIdx} sx={{ mb: 0.5, border: "0.5px solid", borderColor: "divider", borderRadius: 0.5, overflow: "hidden" }}>
                    <Box sx={{ px: 1, py: 0.25, backgroundColor: "#B8CCE4" }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864", fontSize: "0.7rem" }}>
                        {section.title}: {section.total ?? 0}
                      </Typography>
                    </Box>
                    <Grid container sx={{ backgroundColor: "rgba(255,255,255,0.32)" }}>
                      {section.items.map(([label, value], i, arr) => (
                        <Grid
                          item xs
                          key={label}
                          sx={{ p: 0.5, borderRight: i < arr.length-1 ? "0.5px solid" : "none", borderColor: "divider" }}
                        >
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "0.65rem" }}>{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>{value ?? 0}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
                {campaign?.refund_info && (
                  <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 0.5, overflow: "hidden" }}>
                    <Box sx={{ px: 1, py: 0.25, backgroundColor: "#B8CCE4" }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: "#1F3864", fontSize: "0.7rem" }}>Refund</Typography>
                    </Box>
                    <Grid container sx={{ backgroundColor: "rgba(255,255,255,0.32)" }}>
                      {[
                        ["Failed Msgs", campaign.refund_info?.total_messages_failed],
                        ["Rate/Msg (₹)", campaign.refund_info?.rate_per_message?.toFixed(2)],
                        ["Total Refund (₹)", campaign.refund_info?.total_refund_balance?.toFixed(2)],
                      ].map(([label, value], i, arr) => (
                        <Grid item xs key={label} sx={{ p: 0.5, borderRight: i < arr.length-1 ? "0.5px solid" : "none", borderColor: "divider" }}>
                          <Typography variant="caption" sx={{ fontSize: "0.65rem", display: "block" }}>{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>{value ?? 0}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* ── Message Details header + status filter (now server‑side) ── */}
          <Box mb={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="subtitle2" sx={{ fontSize: "0.85rem", fontWeight: 600, display: "inline" }}>
                Message Details
                {(rcsMessageLoading || rcsMessageFetching) && <CircularProgress size={14} sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                Total: {rcsPagination?.totalRecords ?? 0}
              </Typography>
            </Box>

            {/* Filter chips – counts come from the campaign overview */}
            <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
              {categories.map((cat) => (
                <Chip
                  key={cat.key}
                  label={cat.label}
                  size="small"
                  variant={rcsStatusFilter === cat.key ? "filled" : "outlined"}
                  color={cat.color}
                  onClick={() => setRcsStatusFilter(cat.key)}
                  sx={{
                    fontSize: "0.6rem",
                    height: 20,
                    cursor: "pointer",
                    fontWeight: rcsStatusFilter === cat.key ? 700 : 400,
                    bgcolor: rcsStatusFilter === cat.key ? "#7c3aed" : "transparent",
                    color: rcsStatusFilter === cat.key ? "white" : "#7c3aed",
                    borderColor: "#7c3aed",
                    "&:hover": {
                      bgcolor: rcsStatusFilter === cat.key
                        ? "#6d28d9"
                        : "rgba(124,58,237,0.08)",
                    },
                  }}
                />
              ))}

              <FormControl size="small" sx={{ ml: "auto", minWidth: 130 }}>
                <Select
                  value={rcsStatusFilter}
                  onChange={(e) => setRcsStatusFilter(e.target.value)}
                  sx={{ fontSize: "0.65rem", height: 24 }}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.key} value={cat.key} sx={{ fontSize: "0.65rem" }}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Clear button when a filter is active */}
            {rcsStatusFilter !== "Total" && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.secondary" }}>
                  Showing {rcsPagination?.totalRecords ?? 0} record(s) for “{rcsStatusFilter}”
                </Typography>
                <Button
                  size="small"
                  onClick={() => setRcsStatusFilter("Total")}
                  sx={{ fontSize: "0.6rem", p: 0, minWidth: 0, textTransform: "none" }}
                >
                  Clear
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Table – now uses data from the status‑filtered query ── */}
          <TableContainer component={Paper} sx={{ borderRadius: 0.5 }}>
            <Table size="small" sx={{ "& th, & td": { py: 0.5, px: 1, fontSize: "0.7rem" } }}>
              <TableHead sx={{ background: "#B8CCE4" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Send Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Done Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Click</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ background: "#ffffff6b" }}>
                {rcsMessageLoading || rcsMessageFetching ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : rcsMessageData.length > 0 ? (
                  rcsMessageData.map((row) => (
                    <TableRow key={row.sr_no} hover>
                      <TableCell>{row.sr_no}</TableCell>
                      <TableCell>{maskPhone(row.user)}</TableCell>
                      <TableCell>{formatDate(row.send_time)}</TableCell>
                      <TableCell>{formatDate(row.done_time)}</TableCell>
                      <TableCell>{row.provider}</TableCell>
                      <TableCell>
                        <Chip label={row.message_status} size="small" color={getStatusColor(row.message_status)} sx={{ fontSize: "0.65rem", height: 18 }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.65rem", wordBreak: "break-word" }}>{row.reason}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => {
                          setClickReportMobile(row.user);
                          setClickReportPage(1);
                          setClickReportOpen(true);
                        }} title="View clicks">
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                      {rcsStatusFilter !== "Total"
                        ? `No messages with status "${rcsStatusFilter}" found`
                        : "No message details found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination – driven by the status‑filtered query ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={detailRowsPerPage}
                onChange={(e) => { setDetailRowsPerPage(Number(e.target.value)); setDetailPage(1); }}
                sx={{ fontSize: "0.7rem", height: 28 }}
              >
                {[25, 50, 100].map((num) => (
                  <MenuItem key={num} value={num} sx={{ fontSize: "0.7rem" }}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {rcsPagination?.totalPages > 1 && (
              <Pagination
                count={rcsPagination.totalPages}
                page={detailPage}
                onChange={(_, v) => setDetailPage(v)}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </>
      ) : (
        <Typography variant="body2" textAlign="center" py={4}>No details available</Typography>
      )}

      {/* Click Report Dialog – unchanged */}
      <Dialog
        open={clickReportOpen}
        onClose={() => { setClickReportOpen(false); setClickReportMobile(null); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ bgcolor: "#0288d1", color: "white", py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>
            Clicks for {maskPhone(clickReportMobile)}
          </Typography>
          <IconButton size="small" onClick={() => { setClickReportOpen(false); setClickReportMobile(null); }} sx={{ color: "white" }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          {rcsClickReportLoading || rcsClickReportFetching ? (
            <Box textAlign="center" py={4}><CircularProgress size={20} /></Box>
          ) : rcsClickReportError ? (
            <Typography color="error" variant="caption" textAlign="center" py={2}>{rcsClickReportError?.data?.message || "Error"}</Typography>
          ) : rcsClickReport?.status && rcsClickReport.data?.length > 0 ? (
            <>
              <Paper sx={{ p: 1, mb: 1, borderRadius: 1 }}>
                <Grid container spacing={0.5}>
                  <Grid item xs={4}><Typography variant="caption" fontWeight={500}>Campaign:</Typography><Typography variant="caption">{rcsClickReport.campaign_info.campaign_name}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" fontWeight={500}>Template:</Typography><Typography variant="caption">{rcsClickReport.campaign_info.template_name}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" fontWeight={500}>Agent:</Typography><Typography variant="caption">{rcsClickReport.campaign_info.agent_name}</Typography></Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 1, mb: 1, borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={600}>Click Summary</Typography>
                <Grid container spacing={0.5} mt={0.5}>
                  <Grid item xs={3}><Typography variant="caption">Total Clicks</Typography><Typography variant="body2" fontWeight={600}>{rcsClickReport.summary.total_clicks}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption">Unique Btns</Typography><Typography variant="body2" fontWeight={600}>{rcsClickReport.summary.unique_buttons}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption">Last Click</Typography><Typography variant="caption">{formatDate(rcsClickReport.summary.last_click_time)}</Typography></Grid>
                </Grid>
                {rcsClickReport.summary.button_breakdown?.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small" sx={{ "& th, & td": { fontSize: "0.65rem", py: 0.3 } }}>
                      <TableHead><TableRow><TableCell>Button</TableCell><TableCell>Type</TableCell><TableCell align="center">Clicks</TableCell><TableCell>Last Click</TableCell></TableRow></TableHead>
                      <TableBody>
                        {rcsClickReport.summary.button_breakdown.map((btn, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{btn.button_text}</TableCell>
                            <TableCell>{btn.button_type}</TableCell>
                            <TableCell align="center">{btn.total_clicks}</TableCell>
                            <TableCell>{formatDate(btn.last_click_time)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              <Typography variant="caption" fontWeight={600}>Click Details ({rcsClickReport.pagination.totalRecords})</Typography>
              <TableContainer component={Paper} sx={{ mt: 0.5 }}>
                <Table size="small" sx={{ "& th, & td": { fontSize: "0.65rem", py: 0.3 } }}>
                  <TableHead sx={{ background: "#B8CCE4" }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Button</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Click Time</TableCell>
                      <TableCell>Provider</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rcsClickReport.data.map((click) => (
                      <TableRow key={click.sr_no}>
                        <TableCell>{click.sr_no}</TableCell>
                        <TableCell>{click.button_text}</TableCell>
                        <TableCell>{click.button_type}</TableCell>
                        <TableCell>{formatDate(click.click_time)}</TableCell>
                        <TableCell>{click.provider}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {rcsClickReport.pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <FormControl size="small" sx={{ minWidth: 70 }}>
                    <Select value={clickReportRowsPerPage} onChange={(e) => { setClickReportRowsPerPage(Number(e.target.value)); setClickReportPage(1); }} sx={{ fontSize: "0.65rem", height: 24 }}>
                      {[10, 25, 50].map(num => <MenuItem key={num} value={num} sx={{ fontSize: "0.65rem" }}>{num}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Pagination count={rcsClickReport.pagination.totalPages} page={clickReportPage} onChange={(_, v) => setClickReportPage(v)} size="small" />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="caption" textAlign="center" py={2}>No click data available.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RcsCampaignDetailsPage;